import { useEffect } from "react";
import type { CommunityMemberSubscription } from "@/react/types/CommunityMemberSubscription";
import { usePaywallSubscriptionDetailsModal } from "@circle-react-shared/Paywalls/Modals/PaywallSubscriptionDetailsModal";
import { useDefaultColumnsToRender } from "@circle-react-shared/Paywalls/PaywallSubscriptionList/hooks/useDefaultColumnsToRender";

interface SubscriptionData {
  hasFilters: boolean;
  isLoadingSubscriptions: boolean;
  order: Record<string, "desc" | "asc">;
  page: string | number;
  perPage: number;
  refetchSubscriptions: () => Promise<unknown>;
  removeSubscriptionsFromCache: () => void;
  setOrder: (value: Record<string, "desc" | "asc">) => void;
  setPage: (value: string | number) => void;
  subscriptions: CommunityMemberSubscription[];
  totalSubscriptions: number;
}

interface UsePaywallSubscriptionsProps {
  subscriptionData: SubscriptionData;
}

interface CellContextWithSubscription {
  cell: {
    column: { id: string };
    row: { original: CommunityMemberSubscription };
  };
  event?: React.MouseEvent;
}

export const usePaywallSubscriptions = ({
  subscriptionData,
}: UsePaywallSubscriptionsProps) => {
  useEffect(() => {
    if (
      subscriptionData?.subscriptions?.length === 0 &&
      subscriptionData?.totalSubscriptions > 0
    ) {
      subscriptionData?.setPage(1);
      void subscriptionData?.refetchSubscriptions();
    }
  });

  const { defaultColumnsToRender } = useDefaultColumnsToRender();

  const onClickPagination = (currentPage: number) => {
    subscriptionData?.setPage(currentPage);
    void subscriptionData?.refetchSubscriptions();
  };

  const subscriptionDetailModal = usePaywallSubscriptionDetailsModal(undefined);
  const onClickColumn = (cellContext: CellContextWithSubscription) => {
    const {
      cell: {
        column: { id: columnName },
        row: { original: subscription },
      },
    } = cellContext;

    if (columnName !== "customer") {
      void subscriptionDetailModal.show({
        subscription,
        shouldOpenAdminMemberEdit: true,
      });
    }
  };

  const columns = defaultColumnsToRender.map(column => ({
    ...column,
    onClick: onClickColumn,
  }));

  return {
    onClickPagination,
    columnsToRender: columns,
  };
};
